import {
	Grid,
	Skeleton,
	TableContainer,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	useTheme,
	useMediaQuery,
	Card,
	CardContent,
} from '@mui/material';
import {
	CerebroSecondaryButton,
	CerebroStatusIndicatorTableCell,
	CerebroText,
	StatusIndicatorColor,
} from '../../shared/components';
import { ActiveDataRoomUser, DataRoomCall } from '../../shared/models';
import { DateUtils, EnumUtils } from '../../shared/utils';

interface LenderCallsDisplayProps {
	dataRoomGuid: string | undefined;
	isLoading: boolean;
	user: ActiveDataRoomUser | undefined;
	calls: DataRoomCall[] | undefined;
	onEdit: (call: DataRoomCall) => void;
}

export const LenderCallsDisplay: React.FC<LenderCallsDisplayProps> = ({
	dataRoomGuid,
	isLoading,
	user,
	calls,
	onEdit,
}) => {
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

	const getStatusColor = (call: DataRoomCall): StatusIndicatorColor => {
		switch (call.callStatus) {
			case 'Proposed':
				return 'Yellow';
			case 'Scheduled':
				return 'Blue';
			case 'Cancelled':
				return 'Red';
			default:
				return 'Green';
		}
	};

	return isDesktop ? (
		<TableContainer>
			<Table>
				<TableHead>
					<TableRow>
						<TableCell sx={{ width: '5px', padding: '0px' }}></TableCell>
						<TableCell>Date</TableCell>
						<TableCell>Details</TableCell>
						<TableCell>Status</TableCell>
						<TableCell>Actions</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{isLoading && (
						<TableRow>
							<TableCell colSpan={5}>
								<Skeleton height={200} />
								<Skeleton height={200} />
								<Skeleton height={200} />
							</TableCell>
						</TableRow>
					)}
					{calls
						?.sort((a, b) => b.id - a.id)
						.map(call => {
							return (
								<TableRow key={call.guid}>
									<CerebroStatusIndicatorTableCell
										colorType={getStatusColor(call)}
										tooltip={call.callStatus ?? ''}
									/>
									<TableCell>
										{DateUtils.FormatDateTimeStringWithAmPm(call.callDate?.toString()) != null && (
											<>
												{DateUtils.FormatDateTimeStringWithAmPm(call.callDate?.toString())}{' '}
												{call.callTimeZone}
											</>
										)}
									</TableCell>
									<TableCell>{call.callDetails}</TableCell>
									<TableCell>{EnumUtils.GetCallStatusDescription(call.callStatus)}</TableCell>
									<TableCell>
										<CerebroSecondaryButton onClick={() => onEdit(call)}>
											Edit
										</CerebroSecondaryButton>
									</TableCell>
								</TableRow>
							);
						})}
					{calls?.length == 0 && (
						<TableRow>
							<TableCell colSpan={5} sx={{ textAlign: 'center' }}>
								<br />
								<CerebroText>
									There are no scheduled calls, click Schedule Call to get started.
								</CerebroText>
								<br />
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	) : (
		<Grid container spacing={2} alignItems="stretch">
			{calls
				?.sort((a, b) => b.id - a.id)
				.map(call => {
					return (
						<Grid key={call.guid} item xs={12}>
							<Card
								key={call.guid}
								elevation={3}
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									flexDirection: 'column',
									height: '100%',
									marginBottom: 2,
								}}
							>
								<CardContent sx={{ paddingBottom: '0 !important' }}>
									<Grid container sx={{ marginBottom: 2 }}>
										<Grid item xs={8} sx={{ display: 'flex' }}>
											<CerebroText variant="large" component="span" sx={{ fontWeight: 'bold' }}>
												{DateUtils.FormatStringAsDateAndTime(call.callDate?.toString())}
											</CerebroText>
										</Grid>
										<Grid item xs={4} sx={{ textAlign: 'right' }}>
											{EnumUtils.GetCallStatusDescription(call.callStatus)}
										</Grid>
									</Grid>
									<CerebroText gutterBottom sx={{ marginTop: 1 }}>
										{call.callDetails}
									</CerebroText>
									<CerebroSecondaryButton onClick={() => onEdit(call)}>Edit</CerebroSecondaryButton>
								</CardContent>
							</Card>
						</Grid>
					);
				})}
			{calls?.length == 0 && (
				<CerebroText sx={{ marginTop: 5, textAlign: 'center' }}>
					There are no scheduled calls, click Schedule Call to get started.
				</CerebroText>
			)}
		</Grid>
	);
};
